/* eslint-disable no-nested-ternary */
import { useMemo, useState, useCallback } from 'react';
import { capitalCase } from 'change-case';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import {
  ResetTopic2ContentList,
  selectTopic2ContentPageNumber,
  selectGetTopic2ContentState,
  selectTopic2HasMoreContent,
  selectTopic2ContentList,
  UpdateTopic2HasMoreContent,
  UpdateTopic2PageNumber,
} from '../../../store/slices/contentSlice';
import { getTopic2Content } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useFetchCategoryContent } from './useFetchCategoryContent';
import { Content, ContentFeedSection } from '../../../common/models/content/Content';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';

interface Topic2ContentCategoryProps {
  categoryId: string;
  topicName: string;
  viewMorePage?: boolean
  setSomeContentLoadedInitially: (value: boolean) => void
  contentFeedSection: ContentFeedSection
}

export function Topic2ContentCategory({
  categoryId,
  topicName,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}:Topic2ContentCategoryProps) {
  const storeTopic2ContentList = useAppSelector(selectTopic2ContentList);
  const getTopic2ContentState = useAppSelector(selectGetTopic2ContentState);
  const topic2ContentList = useMemo(() => storeTopic2ContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storeTopic2ContentList, contentFeedSection]);

  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { t } = useTranslation();
  const topic2Page = useAppSelector(selectTopic2ContentPageNumber);
  const topic2HasMore = useAppSelector(selectTopic2HasMoreContent);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useTrackCategoryContentVisibility({
    categoryContentList: topic2ContentList,
  });

  const fetchMoreCategoryContent = useFetchCategoryContent({
    viewMorePage,
    contentRequestOptions: { contentTopics: topicName },
    getCategoryContent: getTopic2Content,
    resetCategoryContentList: ResetTopic2ContentList,
    setSomeContentLoadedInitially,
    setOpenErrorSnackbar,
    setInitialFetchDone,
    updateCategoryPageNumber: UpdateTopic2PageNumber,
    updateHasMoreCategoryContent: UpdateTopic2HasMoreContent,
  });

  const isLoading = useMemo(
    () => (getTopic2ContentState === 'pending' && !initialFetchDone),
    [getTopic2ContentState, initialFetchDone],
  );

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  const onNextPage = useCallback(() => {
    fetchMoreCategoryContent(
      topic2Page,
      topic2HasMore,
    );
  }, [topic2Page, topic2HasMore, fetchMoreCategoryContent]);

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      {isLoading ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${topicName}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${categoryId}-content`}
          categoryId={categoryId}
          storeContentList={topic2ContentList}
          onNextPage={onNextPage}
          hasMoreContent={topic2HasMore}
          showSearchBar={true}
        />
      ) : topic2ContentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${topicName}-content`}
          categoryTitle={capitalCase(topicName)}
          storeContentList={topic2ContentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
